/**
 * Where the backup log and the folder's handle live (decision 72; part 24e log, principle 6, table O): a database of
 * their own, `cockatrice-backups`, beside the book's store and never inside it, so the book's records and the store's
 * layout don't change. Each change of a book's record is one read-and-put transaction (O3), so a page and a worker
 * changing the same record never lose each other's change. Values are read at the boundary (`log.ts`).
 */
import { readBookBackups, readMeta } from './log.ts';
import type { BackupMeta, BookBackups } from './log.ts';
import { lockState } from './lock.ts';
import type { BackupLock, LockState } from './lock.ts';

/** The database's name. */
export const BACKUP_DB = 'cockatrice-backups';

/** Its version: 1, with two stores. */
const VERSION = 1;

/** Each book's record, under its book's ID. */
const BOOKS = 'books';

/**
 * The meta record, the folder's handle and the backup lock (part 41b), under {@link META_KEY}, {@link FOLDER_KEY} and
 * {@link LOCK_KEY}: the lock is one more key in the same store, so the database's version doesn't change.
 */
const META = 'meta';
const META_KEY = 'meta';
const FOLDER_KEY = 'folder';
const LOCK_KEY = 'lock';

/** The log, as the page and the worker use it. */
export interface BackupLog {
  /** Every book's record that can be read, by book ID (O5: one that can't is none). */
  readAll(): Promise<ReadonlyMap<string, BookBackups>>;
  /**
   * O3: changes book `bookId`'s record in one transaction: `change` gets the record stored (`fresh()` for none, or for one
   * that can't be read) and gives the record to store, or null to store nothing. Resolves to the record after: the one
   * stored, or the one `change` got.
   */
  update(bookId: string, fresh: () => BookBackups, change: (record: BookBackups) => BookBackups | null): Promise<BookBackups>;
  /** The meta record (its defaults for one that can't be read). */
  meta(): Promise<BackupMeta>;
  /** Changes the meta record in one transaction. */
  changeMeta(change: (meta: BackupMeta) => BackupMeta): Promise<void>;
  /** The folder's stored handle, as stored (read by shape by the caller), or undefined. */
  folder(): Promise<unknown>;
  /** Stores the folder's handle, or forgets it (null). */
  setFolder(handle: unknown): Promise<void>;
  /** Part 41b, E1, E2, E7: the backup lock: none stored is off; one stored that can't be read is unreadable, never off. */
  readLock(): Promise<LockState>;
  /** Part 41b, BP8: stores the backup lock (set, changed, checked), or forgets it (null: turned off). */
  writeLock(lock: BackupLock | null): Promise<void>;
  close(): void;
}

/** A write to the log that didn't commit (W8): the browser aborted it, or a change failed and aborted it. */
export class BackupLogError extends Error {
  override readonly name = 'BackupLogError';

  constructor(cause: unknown) {
    super('a write to the backup log was not kept', { cause });
  }
}

/** A request's result, as a promise. */
function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- the browser's own error, passed through unchanged
      reject(request.error);
    };
  });
}

/**
 * A transaction's end, as a promise: resolved once it commits. A request that fails aborts its transaction (an error not
 * prevented), so the abort is the one failure to hear.
 */
function committed(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve();
    };
    transaction.onabort = () => {
      reject(new BackupLogError(transaction.error));
    };
  });
}

/** What opening the log takes besides the factory. */
export interface OpenLogOptions {
  /** The database name; {@link BACKUP_DB} unless a test isolates itself. */
  readonly name?: string;
  /** This build's version; tests pass another to play a later build. */
  readonly version?: number;
  /** Called once the log has closed itself for a later build's upgrade: whoever keeps it opens it again. */
  readonly gone?: () => void;
}

/**
 * Opens the log on `factory` (`indexedDB`, or a stand-in), creating its stores the first time (version 1 has no other).
 * r1-2: a later build's upgrade is never kept waiting by this connection: it closes on `versionchange` and says so
 * (`gone`). An open that an older connection blocks rejects (N5, tried again later) rather than waiting unseen, and a
 * connection that opens after that rejection is closed at once.
 */
export function openBackupLog(factory: IDBFactory, options: OpenLogOptions = {}): Promise<BackupLog> {
  const request = factory.open(options.name ?? BACKUP_DB, options.version ?? VERSION);
  let refused = false;
  request.onupgradeneeded = () => {
    request.result.createObjectStore(BOOKS);
    request.result.createObjectStore(META);
  };
  const blocked = new Promise<never>((_resolve, reject) => {
    request.onblocked = () => {
      refused = true;
      reject(new BackupLogError('the backup log is held open by an older version of the app'));
    };
  });
  const opened = done(request).then((db) => {
    if (refused) {
      db.close();
      throw new BackupLogError('the backup log opened after it was given up');
    }
    db.onversionchange = () => {
      db.close();
      options.gone?.();
    };
    return indexedDbLog(db);
  });
  // The loser of the race is still settled: its rejection is heard here.
  opened.catch(() => undefined);
  return Promise.race([opened, blocked]);
}

/** The log over an open database. */
function indexedDbLog(db: IDBDatabase): BackupLog {
  /**
   * O3: a read-and-put of the record under `key` in `store`, in one transaction: `read` gives the record a raw value
   * holds (its default for none), `next` the record to put, or null. A change that throws aborts the transaction, so
   * nothing of it is kept.
   */
  const change = async <T>(store: string, key: string, read: (raw: unknown) => T, next: (now: T) => T | null): Promise<T> => {
    const transaction = db.transaction(store, 'readwrite');
    const objects = transaction.objectStore(store);
    let result = read(undefined);
    const get = objects.get(key);
    get.onsuccess = () => {
      try {
        const now = read(get.result);
        const put = next(now);
        if (put !== null) objects.put(put, key);
        result = put ?? now;
      } catch {
        transaction.abort();
      }
    };
    await committed(transaction);
    return result;
  };
  /** Puts `value` under `key` in the meta store, or deletes it (null), in one transaction. */
  const put = async (key: string, value: unknown): Promise<void> => {
    const transaction = db.transaction(META, 'readwrite');
    const objects = transaction.objectStore(META);
    if (value === null) objects.delete(key);
    else objects.put(value, key);
    await committed(transaction);
  };
  return {
    async readAll() {
      const transaction = db.transaction(BOOKS, 'readonly');
      const objects = transaction.objectStore(BOOKS);
      const [keys, values] = await Promise.all([done(objects.getAllKeys()), done(objects.getAll())]);
      const records = new Map<string, BookBackups>();
      keys.forEach((key, i) => {
        const record = readBookBackups(key, values[i]);
        if (record !== null) records.set(record.bookId, record);
      });
      return records;
    },
    update(bookId, fresh, next) {
      return change(BOOKS, bookId, (raw) => readBookBackups(bookId, raw) ?? fresh(), next);
    },
    async meta() {
      return readMeta(await done(db.transaction(META, 'readonly').objectStore(META).get(META_KEY)));
    },
    async changeMeta(next) {
      await change(META, META_KEY, readMeta, next);
    },
    folder() {
      return done(db.transaction(META, 'readonly').objectStore(META).get(FOLDER_KEY));
    },
    async setFolder(handle) {
      await put(FOLDER_KEY, handle);
    },
    async readLock() {
      return lockState(await done(db.transaction(META, 'readonly').objectStore(META).get(LOCK_KEY)));
    },
    async writeLock(lock) {
      await put(LOCK_KEY, lock);
    },
    close() {
      db.close();
    },
  };
}
